import type { ReactNode } from "react";

export interface NavItem {
  href: string;
  label: string;
  icon: ReactNode;
  permission?: string;
  adminOnly?: boolean;
}

export interface NavGroup {
  title?: string;
  divided?: boolean;
  items: NavItem[];
}

/** 仍依赖 Fixture 的页面默认不进入发布导航；只允许本地显式开启联调。 */
const fixturePagesEnabled = process.env.NEXT_PUBLIC_ENABLE_FIXTURE_PAGES === "true";

// SVG 图标（照搬预览 HTML，stroke=currentColor 由 .nav a svg 控制）
const icons: Record<string, ReactNode> = {
  dashboard: (
    <svg viewBox="0 0 24 24">
      <rect x="3" y="3" width="7" height="9" />
      <rect x="14" y="3" width="7" height="5" />
      <rect x="14" y="12" width="7" height="9" />
      <rect x="3" y="16" width="7" height="5" />
    </svg>
  ),
  dailyReport: (
    <svg viewBox="0 0 24 24">
      <path d="M6 3h9l3 3v15H6z" />
      <path d="M14 3v4h4" />
      <path d="M9 10h6M9 14h6M9 18h4" />
    </svg>
  ),
  intelligence: (
    <svg viewBox="0 0 24 24">
      <circle cx="11" cy="11" r="8" />
      <path d="M21 21l-4-4" />
    </svg>
  ),
  upload: (
    <svg viewBox="0 0 24 24">
      <path d="M12 16V4" />
      <path d="M7 9l5-5 5 5" />
      <path d="M5 15v4h14v-4" />
    </svg>
  ),
  signals: (
    <svg viewBox="0 0 24 24">
      <path d="M3 12h18M12 3v18" />
    </svg>
  ),
  research: (
    <svg viewBox="0 0 24 24">
      <path d="M3 3v18h18" />
      <path d="M7 14l4-4 3 3 5-6" />
    </svg>
  ),
  recommendations: (
    <svg viewBox="0 0 24 24">
      <path d="M4 19V9M10 19V5M16 19v-7M22 19V3" />
      <path d="M3 19h19" />
    </svg>
  ),
  monitor: (
    <svg viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  ),
  watchlist: (
    <svg viewBox="0 0 24 24">
      <path d="M5 5h9M5 10h9M5 15h6" />
      <path d="M18 12l1.2 2.5 2.8.4-2 2 .5 2.8-2.5-1.3-2.5 1.3.5-2.8-2-2 2.8-.4z" />
    </svg>
  ),
  sectors: (
    <svg viewBox="0 0 24 24">
      <path d="M12 3l9 5-9 5-9-5z" />
      <path d="M3 12l9 5 9-5M3 16l9 5 9-5" />
    </svg>
  ),
  valuableKol: (
    <svg viewBox="0 0 24 24">
      <circle cx="12" cy="9" r="6" />
      <path d="M8 14l-1 7 5-3 5 3-1-7" />
      <path d="M12 5.5l1 2 2.2.3-1.6 1.6.4 2.2-2-1-2 1 .4-2.2-1.6-1.6 2.2-.3z" />
    </svg>
  ),
  paperTrading: (
    <svg viewBox="0 0 24 24">
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M3 10h18" />
    </svg>
  ),
  picker: (
    <svg viewBox="0 0 24 24">
      <path d="M5 3v4M3 5h4M6 17v4M4 19h4M13 3l2.5 6.5L22 12l-6.5 2.5L13 21l-2.5-6.5L4 12l6.5-2.5z" />
    </svg>
  ),
  strategy: (
    <svg viewBox="0 0 24 24">
      <path d="M4 4h16v16H4z" />
      <path d="M9 9h6v6H9z" />
    </svg>
  ),
  portfolio: (
    <svg viewBox="0 0 24 24">
      <path d="M3 12a9 9 0 1 0 9-9v9z" />
      <path d="M12 3v9h9" />
    </svg>
  ),
  verification: (
    <svg viewBox="0 0 24 24">
      <path d="M9 12l2 2 4-4" />
      <path d="M12 3l7 3v6c0 4-3 7-7 9-4-2-7-5-7-9V6z" />
    </svg>
  ),
  finrisk: (
    <svg viewBox="0 0 24 24">
      <path d="M6 3h9l3 3v15H6z" />
      <path d="M14 3v4h4" />
      <path d="M8 12l2.5 2.5L16 9" />
    </svg>
  ),
  admin: (
    <svg viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="3" />
      <path d="M19 12a7 7 0 0 0-.1-1l2-1.5-2-3.5-2.4 1a7 7 0 0 0-1.7-1l-.3-2.5h-4l-.3 2.5a7 7 0 0 0-1.7 1l-2.4-1-2 3.5 2 1.5a7 7 0 0 0 0 2l-2 1.5 2 3.5 2.4-1a7 7 0 0 0 1.7 1l.3 2.5h4l.3-2.5a7 7 0 0 0 1.7-1l2.4 1 2-3.5-2-1.5c.1-.3.1-.7.1-1z" />
    </svg>
  ),
  users: (
    <svg viewBox="0 0 24 24">
      <circle cx="9" cy="8" r="3" />
      <path d="M3 20v-1a6 6 0 0 1 12 0v1" />
      <path d="M16 5.5a3 3 0 0 1 0 5.8M18 20v-1a5 5 0 0 0-2.5-4.3" />
    </svg>
  ),
  skill: (
    <svg viewBox="0 0 24 24">
      <path d="M12 3l1.5 5.5L19 10l-5.5 1.5L12 17l-1.5-5.5L5 10l5.5-1.5z" />
      <path d="M19 16v5M16.5 18.5h5" />
    </svg>
  ),
  capabilities: (
    <svg viewBox="0 0 24 24">
      <path d="M4 7h16v13H4z" />
      <path d="M8 7V4h8v3M4 12h16M9 16h6" />
    </svg>
  ),
  prompt: (
    <svg viewBox="0 0 24 24">
      <path d="M4 5h16v14H4z" />
      <path d="M8 9h8M8 13h5" />
      <path d="M17 17l3 3" />
    </svg>
  ),
  model: (
    <svg viewBox="0 0 24 24">
      <path d="M8 3h8l5 5v8l-5 5H8l-5-5V8z" />
      <circle cx="9" cy="11" r="1" />
      <circle cx="15" cy="11" r="1" />
      <path d="M8 16h8" />
    </svg>
  ),
  mcp: (
    <svg viewBox="0 0 24 24">
      <rect x="7" y="7" width="10" height="10" rx="2" />
      <path d="M9 7V3M15 7V3M9 21v-4M15 21v-4M7 9H3M7 15H3M21 9h-4M21 15h-4" />
    </svg>
  ),
};

/** 侧栏导航。主工作流组对齐 v39 动线（信息雷达 → AI Skill选股 → 研究）；
 *  「股票推荐」不再独立成菜单，推荐语义收敛进选股页推荐聚合与雷达研究排序。
 *  「持仓与情报台」置于「每天从这里开始」之前：业务方以「我的关注」为每日入口，需靠上展示。*/
export const navGroups: NavGroup[] = [
  {
    items: [
      ...(fixturePagesEnabled
        ? [{ href: "/dashboard", label: "大盘仪表盘", icon: icons.dashboard, permission: "dashboard:read" }]
        : []),
      { href: "/dashboard/daily-report", label: "日报", icon: icons.dailyReport, permission: "daily-report:read" },
    ],
  },
  {
    title: "持仓与情报台",
    divided: true,
    items: [
      { href: "/watchlist/feed", label: "我的关注", icon: icons.watchlist, permission: "radar:read" },
      { href: "/watchlist/sectors", label: "热门板块", icon: icons.sectors, permission: "radar:read" },
      { href: "/watchlist/valuable-kol", label: "大V高价值个股", icon: icons.valuableKol, permission: "radar:read" },
    ],
  },
  {
    title: "每天从这里开始",
    divided: true,
    items: [
      { href: "/signals", label: "信息雷达", icon: icons.intelligence, permission: "radar:read" },
      { href: "/picker", label: "AI Skill选股", icon: icons.picker, permission: "picker:read" },
      { href: "/research", label: "分析中心", icon: icons.research, permission: "research:read" },
      { href: "/research/equity-reports", label: "公司深度报告", icon: icons.dailyReport, permission: "research:read" },
      { href: "/finrisk", label: "财报智评", icon: icons.finrisk, permission: "finrisk:read" },
    ],
  },
  {
    title: "决策与执行",
    divided: true,
    items: [
      { href: "/strategy", label: "B线决策", icon: icons.strategy, permission: "strategy:read" },
    ],
  },
  {
    title: "分析与管理",
    divided: true,
    items: [
      { href: "/paper-trading", label: "模拟交易", icon: icons.paperTrading, permission: "paper-trading:read" },
      { href: "/intelligence/imports", label: "人工上传", icon: icons.upload, permission: "intelligence:import:manage" },
      { href: "/admin/operation-logs", label: "操作日志", icon: icons.verification, adminOnly: true },
      { href: "/admin/identity", label: "用户与权限", icon: icons.users, permission: "identity:user:read" },
      { href: "/admin/configuration", label: "配置中心", icon: icons.model, permission: "admin:llm:manage" },
      { href: "/admin/prompts", label: "提示词管理", icon: icons.prompt, permission: "admin:prompt:manage" },
      { href: "/admin/skills", label: "Skill 发布管理", icon: icons.skill, permission: "admin:skill:manage" },
      { href: "/admin/capabilities", label: "数据能力中心", icon: icons.capabilities, permission: "admin:skill:manage" },
      { href: "/mcp", label: "MCP 中心", icon: icons.mcp, permission: "mcp:read" },
      { href: "/admin/ai-governance", label: "AI 资产与评测", icon: icons.verification, permission: "admin:aigov:read" },
    ],
  },
];

/** Longest matching menu path wins, so /dashboard/daily-report does not inherit dashboard:read. */
export function requiredPermissionForPath(pathname: string): string | undefined {
  return navGroups
    .flatMap((group) => group.items)
    .filter((item) => pathname === item.href || pathname.startsWith(`${item.href}/`))
    .sort((left, right) => right.href.length - left.href.length)[0]?.permission;
}

export function isAdminOnlyPath(pathname: string): boolean {
  return navGroups.flatMap((group) => group.items).some((item) =>
    item.adminOnly && (pathname === item.href || pathname.startsWith(`${item.href}/`)));
}

export function visibleNavGroups(permissions: string[], admin: boolean): NavGroup[] {
  return navGroups.map((group) => ({ ...group, items: group.items.filter((item) =>
    (!item.permission || permissions.includes(item.permission)) && (!item.adminOnly || admin)),
  })).filter((group) => group.items.length > 0);
}
